<script setup lang="ts">
type Props = {
  title?: string;
  description?: string;
};

const { title = '', description = '' } = defineProps<Props>();

const isVisible = defineModel<boolean>('modelValue', {
  default: false,
});

const { bottomMenu } = storeToRefs(useLayoutStore());

const bottomMenuHeightInPixels = computed(() => {
  const bottomMenuHeight = bottomMenu.value.height;

  return `${bottomMenuHeight || 0}px`;
});

function closeModal() {
  isVisible.value = false;
}
</script>

<template>
  <Teleport to="body">
    <section
      v-if="isVisible"
      :class="$style.bottomModal"
    >
      <button
        :class="$style.closeButton"
        @click="closeModal()"
      >
        <NuxtIcon name="close" />
      </button>

      <div :class="$style.container">
        <div :class="$style.inner">
          <slot>
            <div :class="$style.defaultBlock">
              <h3 :class="$style.defaultTitle">
                <BaseCommonSkeleton
                  v-if="!title"
                  :class="$style.skeleton"
                />
                <template v-else>
                  <span>{{ title }}</span>
                </template>
              </h3>

              <h4 :class="$style.defaultDescription">
                <BaseCommonSkeleton
                  v-if="!description"
                  :class="$style.skeleton"
                />
                <template v-else>
                  <span>{{ description }}</span>
                </template>
              </h4>
            </div>

            <slot name="footer" />
          </slot>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<style module lang="postcss">
.bottomModal {
  @apply fixed w-full h-full bottom-0 left-0 z-[800] flex flex-col items-center justify-end;

  margin-bottom: v-bind(bottomMenuHeightInPixels);

  .closeButton {
    @apply relative w-full h-full p-2.5 flex justify-end items-end text-white bg-bg-primary/70 text-2xl;
  }

  .container {
    @apply w-full bg-bg-primary;

    box-shadow: 0 0 2rem rgba(0, 0, 0, 0.4);
  }

  .inner {
    @apply flex flex-col items-start justify-end py-6 pb-8 px-4 max-w-md my-0 mx-auto;
  }

  .defaultBlock {
    @apply w-full flex flex-col justify-center;
  }

  .defaultTitle {
    @apply text-texts w-full text-lg font-bold m-0;

    .skeleton {
      @apply w-full h-5;
    }
  }

  .defaultDescription {
    @apply text-texts/70 w-full text-sm font-normal opacity-70;

    .skeleton {
      @apply w-3/5 h-5 mt-2;
    }
  }
}
</style>
